import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { api } from '../api/client';
import { useAuthStore } from '../stores/authStore';
import { usePageTitle } from '../hooks/usePageTitle';

interface AssessmentSummary {
  id: string;
  overall_score: number;
  verdict: string;
  completed_at: string;
}

interface CvSummary {
  id: string;
  cv_filename: string;
  overall_score: number | null;
  status: string;
  created_at: string;
}

interface EnrolmentSummary {
  id: string;
  programme_type: string;
  package: string;
  status: string;
  current_phase: string;
  current_session: number;
  trainer_name?: string;
  start_date: string;
}

const verdictDisplay: Record<string, string> = {
  strong_fit: 'Strong Fit',
  good_fit: 'Good Fit',
  developing_fit: 'Developing Fit',
  foundation_first: 'Foundation First',
};

export function Dashboard() {
  usePageTitle('Dashboard');
  const user = useAuthStore((s) => s.user);
  const [assessments, setAssessments] = useState<AssessmentSummary[]>([]);
  const [cvReviews, setCvReviews] = useState<CvSummary[]>([]);
  const [enrolments, setEnrolments] = useState<EnrolmentSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get<AssessmentSummary[]>('/assessment/history').catch(() => []),
      api.get<CvSummary[]>('/cv/history').catch(() => []),
      api.get<EnrolmentSummary[]>('/programmes').catch(() => []),
    ])
      .then(([a, c, e]) => {
        setAssessments(a);
        setCvReviews(c);
        setEnrolments(e);
      })
      .finally(() => setLoading(false));
  }, []);

  // Redirect trainers to trainer dashboard
  if (user?.role === 'trainer') {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto mt-10 px-6 pb-20">
          <h1 className="font-display text-3xl font-bold text-mustard-green-dark mb-4">
            Welcome, {user.name.split(' ')[0]}
          </h1>
          <Link
            to="/trainer"
            className="inline-block px-6 py-3 bg-mustard-green-dark text-white rounded-lg font-medium hover:bg-mustard-green transition-colors"
          >
            Go to Trainer Dashboard
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto mt-10 px-6 pb-20">
        <h1 className="font-display text-3xl font-bold text-mustard-green-dark mb-2">
          Welcome back, {user?.name?.split(' ')[0]}
        </h1>
        <p className="text-mustard-charcoal/60 mb-10">
          Your LifeSpark360 dashboard
        </p>

        {loading ? (
          <p className="text-mustard-charcoal/60">Loading...</p>
        ) : (
          <div className="space-y-8">
            {/* Active Programmes */}
            {enrolments.length > 0 && (
              <div>
                <h2 className="font-display text-xl font-bold text-mustard-green-dark mb-4">Your Programmes</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {enrolments.map((e) => (
                    <Link
                      key={e.id}
                      to={`/programme/${e.id}`}
                      className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:border-mustard-green-mid transition-colors"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="font-medium text-mustard-charcoal">
                            {e.programme_type === 'lifespark360' ? 'LifeSpark360' : 'Seedbed'} — {e.package}
                          </p>
                          {e.trainer_name && <p className="text-xs text-mustard-charcoal/50 mt-1">Trainer: {e.trainer_name}</p>}
                        </div>
                        <span className={`text-xs px-2 py-1 rounded font-medium ${e.status === 'active' ? 'bg-green-100 text-green-700' : e.status === 'completed' ? 'bg-mustard-gold/20 text-mustard-gold' : 'bg-gray-100 text-gray-600'}`}>
                          {e.status}
                        </span>
                      </div>
                      <p className="text-sm text-mustard-charcoal/60 mb-2">
                        Phase {e.current_phase} &middot; Session {e.current_session}/{e.programme_type === 'lifespark360' ? 12 : 4}
                      </p>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-mustard-green-dark h-2 rounded-full transition-all"
                          style={{ width: `${(e.current_session / (e.programme_type === 'lifespark360' ? 12 : 4)) * 100}%` }}
                        />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Assessments */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="font-display text-xl font-bold text-mustard-green-dark">Assessments</h2>
                  <a href="https://assessment.lifespark360.com/" className="text-sm text-mustard-green font-medium hover:underline">Take Assessment</a>
                </div>
                {assessments.length === 0 ? (
                  <p className="text-sm text-mustard-charcoal/50">
                    No assessments yet. <a href="https://assessment.lifespark360.com/" className="text-mustard-green hover:underline">Take your first one</a>
                  </p>
                ) : (
                  <div className="space-y-3">
                    {assessments.map((a) => (
                      <div key={a.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <span className="text-2xl font-bold text-mustard-green-dark">{a.overall_score}</span>
                          <span className="text-sm text-mustard-charcoal/50 ml-1">/100</span>
                          <span className="ml-3 text-sm font-medium text-mustard-charcoal/70">{verdictDisplay[a.verdict] || a.verdict}</span>
                        </div>
                        <span className="text-xs text-mustard-charcoal/40">{new Date(a.completed_at).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* CV Reviews */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="font-display text-xl font-bold text-mustard-green-dark">CV Reviews</h2>
                  <Link to="/cv" className="text-sm text-mustard-green font-medium hover:underline">Upload CV</Link>
                </div>
                {cvReviews.length === 0 ? (
                  <p className="text-sm text-mustard-charcoal/50">
                    No CV reviews yet. <Link to="/cv" className="text-mustard-green hover:underline">Upload your CV</Link>
                  </p>
                ) : (
                  <div className="space-y-3">
                    {cvReviews.map((cv) => (
                      <Link key={cv.id} to={`/cv/${cv.id}`} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <div>
                          <p className="text-sm font-medium text-mustard-charcoal">{cv.cv_filename}</p>
                          <span className={`text-xs font-medium ${cv.status === 'complete' ? 'text-green-600' : cv.status === 'failed' ? 'text-red-600' : 'text-amber-600'}`}>
                            {cv.status === 'complete' ? `Score: ${cv.overall_score}/5` : cv.status}
                          </span>
                        </div>
                        <span className="text-xs text-mustard-charcoal/40">{new Date(cv.created_at).toLocaleDateString()}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
